import { useId, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import type { SiteOption } from "../types";

type SearchableSiteOption = SiteOption & {
  country?: string | null;
  country_name?: string | null;
  full_location?: string | null;
  locality?: string | null;
  region?: string | null;
  region_name?: string | null;
};

interface SiteRegionGroup<T extends SearchableSiteOption = SearchableSiteOption> {
  countryLabel: string;
  key: string;
  label: string;
  sites: T[];
}

interface SiteCountryGroup<T extends SearchableSiteOption = SearchableSiteOption> {
  key: string;
  label: string;
  regions: Array<SiteRegionGroup<T>>;
}

export interface SiteSelectorProps {
  defaultOpen?: boolean;
  defaultQuery?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  name?: string;
  onChange: (site: SiteOption | null) => void;
  placeholder?: string;
  sites: SiteOption[];
  value: string | null;
}

const SEARCH_KEYS = ["ArrowDown", "ArrowUp", "End", "Home"] as const;

function normalizeLabelPart(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

function countryLabel(site: SearchableSiteOption): string {
  return normalizeLabelPart(site.country_name ?? site.country) || "Unknown country";
}

function regionLabel(site: SearchableSiteOption): string {
  return normalizeLabelPart(site.region_name ?? site.region) || "Unknown region";
}

function localityLabel(site: SearchableSiteOption): string {
  return normalizeLabelPart(site.locality);
}

function fullLocationLabel(site: SearchableSiteOption): string {
  const fullLocation = normalizeLabelPart(site.full_location);
  if (fullLocation) return fullLocation;
  return [localityLabel(site), regionLabel(site), countryLabel(site)]
    .filter(Boolean)
    .join(", ");
}

function compareLabels(left: string, right: string, locale?: string): number {
  return new Intl.Collator(locale, { sensitivity: "base" }).compare(
    normalizeSearchText(left), normalizeSearchText(right)
  );
}

function compareSites(left: SearchableSiteOption, right: SearchableSiteOption, locale?: string): number {
  return (
    compareLabels(countryLabel(left), countryLabel(right), locale) ||
    compareLabels(regionLabel(left), regionLabel(right), locale) ||
    compareLabels(left.name, right.name, locale) ||
    compareLabels(localityLabel(left), localityLabel(right), locale) ||
    compareLabels(fullLocationLabel(left), fullLocationLabel(right), locale)
  );
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}+/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function filterSiteOptions<T extends SearchableSiteOption>(
  sites: readonly T[],
  query: string
): T[] {
  const needle = normalizeSearchText(query);
  if (!needle) return [...sites];

  return sites.filter((site) =>
    normalizeSearchText(
      [
        site.name,
        site.native_name,
        ...(site.aliases ?? []),
        localityLabel(site),
        fullLocationLabel(site),
        countryLabel(site),
        regionLabel(site)
      ].join(" ")
    ).includes(needle)
  );
}

export function groupSiteOptions<T extends SearchableSiteOption>(
  sites: readonly T[],
  locale?: string
): Array<SiteCountryGroup<T>> {
  const countries = new Map<string, SiteCountryGroup<T>>();

  [...sites].sort((left, right) => compareSites(left, right, locale)).forEach((site) => {
    const nextCountryLabel = countryLabel(site);
    const nextRegionLabel = regionLabel(site);
    const countryKey = normalizeSearchText(nextCountryLabel) || "unknown-country";
    const regionKey = normalizeSearchText(nextRegionLabel) || "unknown-region";
    const existingCountry =
      countries.get(countryKey) ??
      {
        key: countryKey,
        label: nextCountryLabel,
        regions: []
      };
    let existingRegion = existingCountry.regions.find((region) => region.key === regionKey);
    if (!existingRegion) {
      existingRegion = {
        key: regionKey,
        label: nextRegionLabel,
        countryLabel: nextCountryLabel,
        sites: []
      };
      existingCountry.regions.push(existingRegion);
    }
    existingRegion.sites.push(site);
    countries.set(countryKey, existingCountry);
  });

  return [...countries.values()];
}

export function nextActiveIndex(
  currentIndex: number,
  itemCount: number,
  key: (typeof SEARCH_KEYS)[number]
): number {
  if (itemCount <= 0) return -1;
  if (key === "Home") return 0;
  if (key === "End") return itemCount - 1;
  if (key === "ArrowDown") {
    return currentIndex < 0 ? 0 : Math.min(itemCount - 1, currentIndex + 1);
  }
  return currentIndex < 0 ? itemCount - 1 : Math.max(0, currentIndex - 1);
}

function optionId(baseId: string, slug: string): string {
  return `${baseId}-option-${slug}`;
}

export function SiteSelector({
  defaultOpen = false,
  defaultQuery = "",
  disabled = false,
  id,
  label = "Launch",
  name,
  onChange,
  placeholder = "Choose a launch",
  sites,
  value
}: SiteSelectorProps) {
  const { t, i18n } = useTranslation();
  const generatedId = useId();
  const inputId = id ?? `site-selector-${generatedId}`;
  const listboxId = `${inputId}-listbox`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedSite = sites.find((site) => site.slug === value) ?? null;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [query, setQuery] = useState(defaultQuery);
  const [activeIndex, setActiveIndex] = useState(() => {
    if (!defaultOpen) return -1;
    const initialMatches = groupSiteOptions(filterSiteOptions(sites, defaultQuery), i18n.language).flatMap(
      (country) => country.regions.flatMap((region) => region.sites)
    );
    const selectedIndex =
      value === null ? -1 : initialMatches.findIndex((site) => site.slug === value);
    return selectedIndex >= 0 ? selectedIndex : initialMatches.length > 0 ? 0 : -1;
  });

  const filteredSites = filterSiteOptions(sites, query);
  const groupedSites = groupSiteOptions(filteredSites, i18n.language);
  const orderedSites = groupedSites.flatMap((country) =>
    country.regions.flatMap((region) => region.sites)
  );
  const optionIndexBySlug = new Map(
    orderedSites.map((site, index) => [site.slug, index] as const)
  );
  const activeSite =
    activeIndex >= 0 && activeIndex < orderedSites.length ? orderedSites[activeIndex] : null;
  const displayValue = query !== "" ? query : selectedSite?.name ?? "";

  const closeList = () => {
    setIsOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };

  const openList = () => {
    if (disabled) return;
    setIsOpen(true);
    setActiveIndex((currentIndex) => {
      if (currentIndex >= 0 && currentIndex < orderedSites.length) return currentIndex;
      const selectedIndex =
        value === null ? -1 : orderedSites.findIndex((site) => site.slug === value);
      return selectedIndex >= 0 ? selectedIndex : orderedSites.length > 0 ? 0 : -1;
    });
  };

  const selectSite = (site: SiteOption | null) => {
    onChange(site);
    closeList();
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (rootRef.current?.contains(event.relatedTarget as Node | null)) return;
    closeList();
  };

  const handleInputChange = (nextValue: string) => {
    if (disabled) return;
    setIsOpen(true);
    setQuery(nextValue);
    const nextMatches = groupSiteOptions(filterSiteOptions(sites, nextValue), i18n.language).flatMap(
      (country) => country.regions.flatMap((region) => region.sites)
    );
    setActiveIndex(nextMatches.length > 0 ? 0 : -1);
    if (nextValue.trim() === "" && selectedSite) {
      onChange(null);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (SEARCH_KEYS.includes(event.key as (typeof SEARCH_KEYS)[number])) {
      event.preventDefault();
      if (!isOpen) setIsOpen(true);
      setActiveIndex((currentIndex) =>
        nextActiveIndex(
          currentIndex,
          orderedSites.length,
          event.key as (typeof SEARCH_KEYS)[number]
        )
      );
      return;
    }

    if (event.key === "Enter") {
      if (!isOpen) return;
      event.preventDefault();
      if (activeSite) selectSite(activeSite);
      return;
    }

    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      closeList();
    }
  };

  return (
    <div className="site-selector" onBlur={handleBlur} ref={rootRef}>
      <label className="site-selector-label" htmlFor={inputId}>
        <span>{label}</span>
      </label>
      <div className="site-selector-control">
        <input
          aria-activedescendant={isOpen && activeSite ? optionId(inputId, activeSite.slug) : undefined}
          aria-autocomplete="list"
          aria-controls={isOpen && filteredSites.length > 0 ? listboxId : undefined}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          autoComplete="off"
          className="site-selector-input"
          disabled={disabled}
          id={inputId}
          name={name}
          onChange={(event) => handleInputChange(event.target.value)}
          onClick={openList}
          onFocus={(event) => {
            openList();
            event.currentTarget.select();
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          role="combobox"
          spellCheck={false}
          type="text"
          value={displayValue}
        />
      </div>

      {isOpen && filteredSites.length > 0 ? (
        <div className="site-selector-results" id={listboxId} role="listbox">
          {groupedSites.map((country) => (
            <div className="site-selector-country" key={country.key} role="presentation">
              <div className="site-selector-country-label">{country.label}</div>
              {country.regions.map((region) => {
                const regionHeadingId = `${inputId}-${country.key}-${region.key}-heading`;
                return (
                  <div
                    aria-labelledby={regionHeadingId}
                    className="site-selector-region"
                    key={`${country.key}-${region.key}`}
                    role="group"
                  >
                    <div className="site-selector-region-label" id={regionHeadingId}>
                      {region.label}
                    </div>
                    {region.sites.map((site) => {
                      const siteIndex = optionIndexBySlug.get(site.slug) ?? -1;
                      const isActive = siteIndex === activeIndex;
                      const isSelected = site.slug === value;
                      return (
                        <div
                          aria-selected={isSelected}
                          className="site-selector-option"
                          id={optionId(inputId, site.slug)}
                          key={site.slug}
                          onClick={() => selectSite(site)}
                          onMouseDown={(event) => event.preventDefault()}
                          role="option"
                        >
                          <span data-active={isActive ? "true" : undefined}>
                            {site.native_name || site.name}
                          </span>
                          <small>{fullLocationLabel(site)}</small>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}

      {isOpen && filteredSites.length === 0 ? (
        <div aria-live="polite" className="site-selector-empty" role="status">
          {t("controls.noLaunches")}: “{query.trim() || placeholder}”.
        </div>
      ) : null}
    </div>
  );
}
